import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import DropdownArrow from '../__partials/dropdown-arrow';
import NoMatchingResults from '../__partials/no-matching-results';
import Option from '../__partials/option';
import OptionGroup from '../__partials/option-group';
import Dismiss from '../../__partials/dismiss';
import Label from '../../forms/__partials/label';
import HelpText from '../../forms/__partials/help-text';
import idGenerator from '../../__utils/idGenerator';
import classNames from 'classnames';
import {
  isItemSelected,
  isItemHighlighted,
  generateIdGenerator,
  getMaxHeightStyles,
  itemToString,
  filterOnHead,
  filterOnFullString,
  KeyCodes,
  shouldRenderOptionGroups,
} from '../../__utils/selectHelpers';
import { OptionsPropType } from '../../__utils/selectPropTypes';

import '../style.scss';

export default class LookupSelect extends Component {
  constructor(props) {
    super(props);

    this.id = this.props.id || idGenerator();
    this.qaId = this.props.qaId || this.id;

    this.idGenerator = generateIdGenerator(this.id);
    this.qaIdGenerator = generateIdGenerator(this.qaId);

    let selItem;
    if (this.props.defaultValue) {
      if (['string', 'number'].includes(typeof this.props.defaultValue)) {
        let options = this.props.options;
        if (!Array.isArray(options)) {
          options = Object.keys(options).reduce((acc, cur) => acc.concat(options[cur].options), []);
        }
        selItem = options.find(option => option.value === this.props.defaultValue);
      } else {
        // defaultValue is an object, assume it's an option
        selItem = this.props.defaultValue;
      }
    }

    this.state = {
      selectedItem: selItem,
    };
  }

  /**
   * Its possible to not provide a value prop to this component.
   * This is a quick way to check if there was a value prop provided
   */
  shouldUseValueProp() {
    return this.props.hasOwnProperty('value');
  }

  onChange = (selectedItem = null) => {
    // state.selectedItem is not used when this is a controlled component
    if (this.shouldUseValueProp()) {
      this.props.onChange && this.props.onChange(selectedItem);
      return;
    }

    this.setState({ selectedItem }, () => {
      this.props.onChange && this.props.onChange(this.state.selectedItem);
    });
  };

  onInputValueChange = inputValue => {
    this.props.onSearchChange && this.props.onSearchChange(inputValue);
  };

  onDownshiftStateChange = newDownShiftState => {
    if (newDownShiftState.isOpen && this.props.hasOwnProperty('onMenuOpen')) {
      this.props.onMenuOpen();
    }
  };

  getFilterFunction(selectedOption) {
    if (this.props.showAllOptions && selectedOption) {
      return options => options;
    }
    return this.props.matchHead ? filterOnHead : filterOnFullString;
  }

  renderOptionList(downshiftProps, options, baseIndex = 0) {
    return options.map((option, itemIndex) => {
      const index = baseIndex + itemIndex;
      const idDescriptor = `${option.value}-option`;

      const { getItemProps, selectedItem, highlightedIndex, inputValue } = downshiftProps;

      return (
        <Option
          {...getItemProps({ item: option })}
          optionData={option}
          isSelected={isItemSelected(selectedItem, option)}
          isHighlighted={isItemHighlighted(highlightedIndex, index)}
          matchData={inputValue}
          matchHead={this.props.matchHead}
          key={option.value}
          qaId={this.qaIdGenerator(idDescriptor)}
        >
          {this.props.customOption}
        </Option>
      );
    });
  }

  renderOptionGroups(downshiftProps, optionGroups) {
    return Object.keys(optionGroups).reduce(
      (result, groupKey) => {
        const group = optionGroups[groupKey];

        const filteredOptions = this.getFilterFunction(downshiftProps.selectedItem)(
          group.options,
          downshiftProps.inputValue
        );
        if (filteredOptions.length === 0) return result;

        const idDescriptor = `${groupKey}-options-group`;

        const optionGroup = (
          <OptionGroup
            key={groupKey}
            title={group.label}
            id={this.idGenerator(idDescriptor)}
            qaId={this.qaIdGenerator(idDescriptor)}
          >
            {this.renderOptionList(downshiftProps, filteredOptions, result.itemIndex)}
          </OptionGroup>
        );

        return {
          groups: [...result.groups, optionGroup],
          itemIndex: result.itemIndex + filteredOptions.length,
        };
      },
      { groups: [], itemIndex: 0 }
    ).groups;
  }

  renderOptions(downshiftProps, options) {
    const optionsList = shouldRenderOptionGroups(options)
      ? this.renderOptionGroups(downshiftProps, options)
      : this.renderOptionList(
        downshiftProps,
        this.getFilterFunction(downshiftProps.selectedItem)(options, downshiftProps.inputValue)
      );

    return optionsList.length > 0 ? optionsList : <NoMatchingResults text={this.props.noMatchingResultsText} />;
  }

  render() {
    const {
      options,
      label,
      hideLabel,
      helpText,
      hasError,
      required,
      disabled,
      maxHeight,
      placeholder,
      value,
      name,
      preserveSelectionOnBackspace,
    } = this.props;

    const formItemClasses = classNames('uni-form__item', {
      'uni-form__item--is-error': hasError,
      'uni-form__item--is-disabled': disabled,
    });

    let valueOption = value;
    if (this.shouldUseValueProp() && ['number', 'string'].includes(typeof this.props.value)) {
      let flattenedOptions = this.props.options;
      if (!Array.isArray(flattenedOptions)) {
        flattenedOptions = Object.keys(flattenedOptions).reduce(
          (acc, cur) => acc.concat(flattenedOptions[cur].options),
          []
        );
      }
      valueOption = flattenedOptions.find(option => option.value === this.props.value);
    }

    return (
      <Downshift
        onChange={this.onChange}
        onInputValueChange={this.onInputValueChange}
        itemToString={itemToString}
        id={this.id}
        selectedItem={this.shouldUseValueProp() ? valueOption : this.state.selectedItem}
        onStateChange={this.onDownshiftStateChange}
      >
        {downshiftProps => {
          const { isOpen, highlightedIndex, selectedItem, inputValue } = downshiftProps;

          /* eslint-disable no-return-assign */
          const defaultInputProps = downshiftProps.getInputProps({
            id: this.idGenerator('input'),
            onBlur: e => (e.preventDownshiftDefault = true),
            placeholder,
          });
          /* eslint-enable no-return-assign */

          const onInputKeyDown = e => {
            const nothingIsHighlighted = highlightedIndex === null;
            const inputIsEmpty = !inputValue;
            const hasSelectedItem = selectedItem !== null;

            if (!inputIsEmpty && hasSelectedItem && e.keyCode === KeyCodes.BACKSPACE) {
              !preserveSelectionOnBackspace && downshiftProps.clearSelection();
              downshiftProps.openMenu();
              return;
            }

            if (
              // If the key is something other than enter or space, we want to fail
              // this evaluation immediately
              (e.keyCode === KeyCodes.ENTER || e.keyCode === KeyCodes.SPACE) &&
              // Only toggle the menu (on enter or space) if the menu is not open
              // or it there is no highlighted item (so that it can be selected on enter)
              // or the input is empty and nothing is selected because enter should toggle it anyway,
              //    and space would create a leading space otherwise
              // or if an item is selected because they need to clear the item to start typing again anyway
              // which is taken care of by the backspace check above
              (!isOpen ||
                (nothingIsHighlighted && inputIsEmpty && !hasSelectedItem) ||
                (hasSelectedItem && e.keyCode === KeyCodes.ENTER))
            ) {
              e.preventDefault();
              downshiftProps.toggleMenu();
              return;
            }

            defaultInputProps.onKeyDown(e);
          };

          const inputProps = {
            ...defaultInputProps,
            disabled: disabled,
            onKeyDown: onInputKeyDown,
          };

          const selectClasses = classNames('uni-select', {
            'uni-select--is-open': isOpen,
          });

          return (
            <div className={formItemClasses}>
              {label && (
                <Label label={label} required={required} name={this.idGenerator('input')} hideLabel={hideLabel} />
              )}
              <div
                className={selectClasses}
                id={this.id}
                data-qa-id={this.qaId}
                onClick={downshiftProps.toggleMenu}
              >
                <div className="uni-select__input-container">
                  <input {...inputProps} className="uni-select__input" />

                  <span className="uni-select__input-controls">
                    {inputValue && (
                      <Dismiss
                        size="small"
                        className="uni-select__input-dismiss"
                        onClick={() => {
                          downshiftProps.clearSelection();
                          downshiftProps.openMenu();
                        }}
                      />
                    )}

                    <DropdownArrow />
                  </span>
                </div>

                {!this.shouldUseValueProp() && (
                  <input
                    type="hidden"
                    value={selectedItem ? selectedItem.value : ''}
                    name={name}
                    id={this.idGenerator('hidden-input')}
                    data-qa-id={this.qaIdGenerator('hidden-input')}
                  />
                )}
                <ul
                  className="uni-select__items"
                  id={this.idGenerator('options-list')}
                  style={getMaxHeightStyles(maxHeight, isOpen)}
                  onClick={() => downshiftProps.toggleMenu()}
                >
                  {this.renderOptions(downshiftProps, options)}
                </ul>
              </div>

              {helpText && <HelpText name={this.idGenerator('input')} text={helpText} hasError={hasError} />}
            </div>
          );
        }}
      </Downshift>
    );
  }
}

LookupSelect.propTypes = {
  options: OptionsPropType,
  customOption: PropTypes.any,
  id: PropTypes.string,
  qaId: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helpText: PropTypes.string,
  hasError: PropTypes.bool,
  matchHead: PropTypes.bool,
  maxHeight: PropTypes.string,
  noMatchingResultsText: PropTypes.any,
  onChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  placeholder: PropTypes.string,
  preserveSelectionOnBackspace: PropTypes.bool,
  onMenuOpen: PropTypes.func,
  showAllOptions: PropTypes.bool,
};

LookupSelect.defaultProps = {
  matchHead: false,
  noMatchingResultsText: 'No matching results',
};



// WEBPACK FOOTER //
// ./src/selects/lookup-select/index.jsx